/**
 * `/cash/movements/new` — S31 + S31a kind-aware composer.
 *
 * Three-phase UX:
 *   1. Kind picker (S31a) — operator picks one of the 14 V1 kinds. The
 *      2 V1.x kinds appear muted with a "Lands in V1.x" tag.
 *   2. Kind-aware form (S31) — shows only the fields the picked kind
 *      needs (from/to account, counterparty, amount, narration, etc.).
 *   3. Submit → `cashMovements.post`. Approval-gate flow surfaces as a
 *      `PRECONDITION_FAILED` from the wire; we surface a friendly note
 *      and (V1.x) drop a `pending_approvals` row.
 *
 * @see prototype/cash/cash-movement-new.html — V1.1 design baseline
 * @see docs/spec/cash-module-screens-spec.md §5 (S31)
 */
import { useEffect, useMemo, useState } from "react";
import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import {
  ArrowLeft,
  ArrowLeftRight,
  ArrowUpFromLine,
  Banknote,
  Coins,
  CreditCard,
  FileText,
  Hand,
  Landmark,
  Package,
  Send,
  Wallet,
} from "lucide-react";
import { Alert, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@flint/ui";
import { trpc } from "../../../../lib/trpc.ts";
import { WorkspaceShell } from "../../../../features/workspace/index.ts";

export const Route = createFileRoute("/(workspace)/cash/movements/new")({
  component: NewCashMovementPage,
});

type Kind =
  | "transfer"
  | "refund_customer"
  | "statutory_payment"
  | "owner_contribution"
  | "owner_drawing"
  | "loan_disbursement"
  | "loan_repayment"
  | "interest_paid"
  | "stock_writeoff"
  | "petty_cash_expense"
  | "cash_count_adjustment"
  | "deposit_paid"
  | "deposit_received"
  | "bank_charge"
  | "refund_vendor"
  | "interest_received";

interface KindMeta {
  kind: Kind;
  title: string;
  sub: string;
  icon: React.ReactNode;
  v1: boolean;
  alwaysStepUp?: boolean;
}

const KINDS: KindMeta[] = [
  {
    kind: "transfer",
    title: "Transfer between accounts",
    sub: "Move money between your bank / cash / card",
    icon: <ArrowLeftRight size={18} strokeWidth={2} />,
    v1: true,
  },
  {
    kind: "statutory_payment",
    title: "Statutory payment",
    sub: "TDS / GST / PF / PT challan deposit",
    icon: <FileText size={18} strokeWidth={2} />,
    v1: true,
  },
  {
    kind: "petty_cash_expense",
    title: "Petty cash expense",
    sub: "Drawer spend with category (postage, conveyance…)",
    icon: <Wallet size={18} strokeWidth={2} />,
    v1: true,
  },
  {
    kind: "bank_charge",
    title: "Bank / card charge",
    sub: "FX markup, annual fee, wire fee, ATM fee…",
    icon: <CreditCard size={18} strokeWidth={2} />,
    v1: true,
  },
  {
    kind: "owner_drawing",
    title: "Owner drawing",
    sub: "Money out to a founder · always step-up",
    icon: <Hand size={18} strokeWidth={2} />,
    v1: true,
    alwaysStepUp: true,
  },
  {
    kind: "owner_contribution",
    title: "Owner contribution",
    sub: "Money in from a founder",
    icon: <Landmark size={18} strokeWidth={2} />,
    v1: true,
  },
  {
    kind: "loan_disbursement",
    title: "Loan disbursement",
    sub: "Money in from a lender · always step-up",
    icon: <Banknote size={18} strokeWidth={2} />,
    v1: true,
    alwaysStepUp: true,
  },
  {
    kind: "loan_repayment",
    title: "Loan repayment (principal)",
    sub: "EMI principal portion · always step-up",
    icon: <Banknote size={18} strokeWidth={2} />,
    v1: true,
    alwaysStepUp: true,
  },
  {
    kind: "interest_paid",
    title: "Loan interest",
    sub: "EMI interest portion · always step-up",
    icon: <Banknote size={18} strokeWidth={2} />,
    v1: true,
    alwaysStepUp: true,
  },
  {
    kind: "refund_customer",
    title: "Customer refund",
    sub: "Refund against a posted credit note",
    icon: <ArrowUpFromLine size={18} strokeWidth={2} />,
    v1: true,
  },
  {
    kind: "cash_count_adjustment",
    title: "Cash count adjustment",
    sub: "Drawer count over / short reconciliation",
    icon: <Coins size={18} strokeWidth={2} />,
    v1: true,
  },
  {
    kind: "deposit_paid",
    title: "Deposit paid",
    sub: "Lease / utility / vendor refundable deposit",
    icon: <Package size={18} strokeWidth={2} />,
    v1: true,
  },
  {
    kind: "deposit_received",
    title: "Deposit received",
    sub: "Customer / tenant refundable deposit",
    icon: <Package size={18} strokeWidth={2} />,
    v1: true,
  },
  {
    kind: "stock_writeoff",
    title: "Stock write-off",
    sub: "Notional inventory loss (no cash moves)",
    icon: <Package size={18} strokeWidth={2} />,
    v1: true,
  },
  // V1.x
  {
    kind: "refund_vendor",
    title: "Vendor refund",
    sub: "Refund from a vendor (debit note)",
    icon: <ArrowUpFromLine size={18} strokeWidth={2} />,
    v1: false,
  },
  {
    kind: "interest_received",
    title: "Interest received",
    sub: "Passive income from FDs / bank interest",
    icon: <Banknote size={18} strokeWidth={2} />,
    v1: false,
  },
];

function NewCashMovementPage() {
  const navigate = useNavigate();
  const [kind, setKind] = useState<Kind | null>(null);
  const accountsQ = trpc.bankAccounts.list.useQuery({});

  if (!kind) {
    return <KindPicker onPick={setKind} />;
  }
  if (!accountsQ.data) {
    return (
      <WorkspaceShell breadcrumbs={[{ label: "Cash" }, { label: "New movement" }]}>
        <p className="text-sm text-muted">Loading accounts…</p>
      </WorkspaceShell>
    );
  }

  return (
    <ComposerForm
      kind={kind}
      onBack={() => setKind(null)}
      accounts={accountsQ.data.rows.filter((r) => r.archivedAt === null)}
      onSuccess={() => navigate({ to: "/cash/bank-accounts" })}
    />
  );
}

// ── Kind picker (S31a) ────────────────────────────────────────────

function KindPicker({ onPick }: { onPick: (k: Kind) => void }) {
  const grouped = useMemo(() => {
    const v1 = KINDS.filter((k) => k.v1);
    const v1x = KINDS.filter((k) => !k.v1);
    return { v1, v1x };
  }, []);
  return (
    <WorkspaceShell breadcrumbs={[{ label: "Cash" }, { label: "Record cash movement" }]}>
      <div className="max-w-3xl mx-auto flex flex-col gap-6">
        <header className="flex items-center gap-3">
          <Link
            to="/cash/bank-accounts"
            className="inline-grid place-items-center w-9 h-9 rounded-sm border border-line bg-paper text-ink-2 hover:bg-bg no-underline"
            aria-label="Back"
          >
            <ArrowLeft size={16} strokeWidth={2} />
          </Link>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-ink leading-tight">
              Record a cash movement
            </h1>
            <p className="mt-1 text-sm text-muted">
              Pick what kind of money event you're recording. The form adapts to the kind.
            </p>
          </div>
        </header>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {grouped.v1.map((k) => (
            <KindCard key={k.kind} meta={k} onPick={onPick} />
          ))}
        </div>

        <div>
          <h2 className="text-xs font-semibold uppercase tracking-wider text-muted mb-3">
            Lands in V1.x
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 opacity-60">
            {grouped.v1x.map((k) => (
              <div
                key={k.kind}
                className="text-left p-3 rounded-md border border-dashed border-line-2 bg-bg/30 flex gap-3 items-start"
              >
                <div className="w-9 h-9 rounded-sm bg-paper text-muted grid place-items-center flex-shrink-0">
                  {k.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-ink-2">{k.title}</div>
                  <div className="text-xs text-muted mt-0.5">{k.sub}</div>
                </div>
                <span className="px-1.5 py-px rounded-sm bg-paper text-xs font-bold uppercase tracking-wider text-muted border border-line">
                  V1.x
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </WorkspaceShell>
  );
}

function KindCard({ meta, onPick }: { meta: KindMeta; onPick: (k: Kind) => void }) {
  return (
    <button
      type="button"
      onClick={() => onPick(meta.kind)}
      className="text-left p-3 rounded-md border-2 border-line bg-paper hover:border-sky-600 hover:shadow-md transition-all flex gap-3 items-start"
    >
      <div className="w-9 h-9 rounded-sm bg-sky-50 text-sky-700 grid place-items-center flex-shrink-0">
        {meta.icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-ink">{meta.title}</span>
          {meta.alwaysStepUp ? (
            <span className="px-1 py-px rounded-sm bg-amber-50 text-amber-700 text-xs font-bold uppercase tracking-wider border border-amber-100">
              Step-up
            </span>
          ) : null}
        </div>
        <div className="text-xs text-muted mt-0.5">{meta.sub}</div>
      </div>
    </button>
  );
}

// ── Composer form (S31) ───────────────────────────────────────────

interface AccountOption {
  id: string;
  name: string;
  type: "bank" | "cash" | "credit_card";
}

function ComposerForm({
  kind,
  accounts,
  onBack,
  onSuccess,
}: {
  kind: Kind;
  accounts: ReadonlyArray<AccountOption>;
  onBack: () => void;
  onSuccess: () => void;
}) {
  const [fromAccountId, setFromAccountId] = useState("");
  const [toAccountId, setToAccountId] = useState("");
  const [amountRupees, setAmountRupees] = useState("");
  const [valueDate, setValueDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [narration, setNarration] = useState("");
  const [counterpartyLabel, setCounterpartyLabel] = useState("");
  const [expenseCategory, setExpenseCategory] = useState<
    | "postage"
    | "conveyance"
    | "travel"
    | "refreshments"
    | "office_supplies"
    | "printing_stationery"
    | "repairs_maintenance"
    | "sundries"
  >("sundries");
  const [bankChargeSubtype, setBankChargeSubtype] = useState<
    | "fx_markup"
    | "card_annual_fee"
    | "late_fee"
    | "wire_transfer_fee"
    | "atm_fee"
    | "account_maintenance"
    | "other"
  >("other");
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  // Owner kinds (contribution / drawing) need a `membership` counterparty —
  // the domain rejects them otherwise. Resolve the company's members and
  // default to the signed-in user's own membership.
  const [ownerMembershipId, setOwnerMembershipId] = useState("");
  const teamQ = trpc.team.list.useQuery({}, { enabled: needsOwner(kind) });
  const meQ = trpc.identity.me.useQuery({}, { enabled: needsOwner(kind) });
  const ownerOptions = useMemo(
    () =>
      (teamQ.data?.members ?? []).map((m) => ({
        value: m.membershipId,
        label: `${m.user.name} · ${m.role}`,
      })),
    [teamQ.data],
  );
  useEffect(() => {
    if (!needsOwner(kind) || ownerMembershipId || !teamQ.data) return;
    const mine = teamQ.data.members.find((m) => m.userId === meQ.data?.user.id);
    const pick = mine ?? teamQ.data.members[0];
    if (pick) setOwnerMembershipId(pick.membershipId);
  }, [kind, ownerMembershipId, teamQ.data, meQ.data]);

  const meta = KINDS.find((k) => k.kind === kind)!;
  const post = trpc.cashMovements.post.useMutation({
    onSuccess: (r) => {
      if (r.awaitingApproval) {
        setInfo(
          `This movement needs founder approval (${r.awaitingApproval.summary}). The approval inbox UX lands in V1.0.x.`,
        );
      } else {
        onSuccess();
      }
    },
    onError: (err) => {
      setError(err.message);
    },
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    const amountPaise = String(Math.round(Number(amountRupees || "0") * 100));
    const payload: Parameters<typeof post.mutate>[0] = {
      kind,
      amountPaise,
      valueDate,
      narration,
    };
    if (fromAccountId) payload.fromAccountId = fromAccountId;
    if (toAccountId) payload.toAccountId = toAccountId;
    if (counterpartyLabel) payload.counterpartyLabel = counterpartyLabel;
    if (needsOwner(kind)) {
      payload.counterpartyType = "membership";
      payload.counterpartyId = ownerMembershipId;
    }
    if (kind === "petty_cash_expense") payload.expenseCategory = expenseCategory;
    if (kind === "bank_charge") payload.bankChargeSubtype = bankChargeSubtype;
    post.mutate(payload);
  };

  return (
    <WorkspaceShell
      breadcrumbs={[{ label: "Cash" }, { label: "Record movement" }, { label: meta.title }]}
    >
      <div className="max-w-2xl mx-auto flex flex-col gap-5">
        <header className="flex items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            className="inline-grid place-items-center w-9 h-9 rounded-sm border border-line bg-paper text-ink-2 hover:bg-bg"
            aria-label="Back to kind picker"
          >
            <ArrowLeft size={16} strokeWidth={2} />
          </button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-ink leading-tight">
              {meta.title}
            </h1>
            <p className="mt-1 text-sm text-muted">{meta.sub}</p>
          </div>
        </header>

        <form
          onSubmit={submit}
          className="bg-paper border border-line rounded-md shadow-sm p-5 flex flex-col gap-4"
        >
          {needsFrom(kind) ? (
            <FieldSelect
              label="From account"
              value={fromAccountId}
              onChange={setFromAccountId}
              options={accounts.map((a) => ({ value: a.id, label: `${a.name} · ${a.type}` }))}
              required
            />
          ) : null}
          {needsTo(kind) ? (
            <FieldSelect
              label="To account"
              value={toAccountId}
              onChange={setToAccountId}
              options={accounts.map((a) => ({ value: a.id, label: `${a.name} · ${a.type}` }))}
              required
            />
          ) : null}
          {needsOwner(kind) ? (
            <FieldSelect
              label={kind === "owner_contribution" ? "Contributing owner" : "Owner taking funds"}
              value={ownerMembershipId}
              onChange={setOwnerMembershipId}
              options={ownerOptions}
              required
            />
          ) : null}
          {needsCounterpartyLabel(kind) ? (
            <FieldText
              label="Counterparty"
              value={counterpartyLabel}
              onChange={setCounterpartyLabel}
              placeholder={
                kind === "loan_disbursement" ||
                kind === "loan_repayment" ||
                kind === "interest_paid"
                  ? "Lender name (e.g. HDFC Bank · OD-9881)"
                  : "Free text"
              }
              required
            />
          ) : null}
          {kind === "petty_cash_expense" ? (
            <FieldSelect
              label="Expense category"
              value={expenseCategory}
              onChange={(v) => setExpenseCategory(v as typeof expenseCategory)}
              options={[
                { value: "postage", label: "Postage" },
                { value: "conveyance", label: "Conveyance" },
                { value: "travel", label: "Travel" },
                { value: "refreshments", label: "Refreshments / Tea & Snacks" },
                { value: "office_supplies", label: "Office Supplies" },
                { value: "printing_stationery", label: "Printing & Stationery" },
                { value: "repairs_maintenance", label: "Repairs & Maintenance" },
                { value: "sundries", label: "Sundries" },
              ]}
              required
            />
          ) : null}
          {kind === "bank_charge" ? (
            <FieldSelect
              label="Charge type"
              value={bankChargeSubtype}
              onChange={(v) => setBankChargeSubtype(v as typeof bankChargeSubtype)}
              options={[
                { value: "fx_markup", label: "FX markup" },
                { value: "card_annual_fee", label: "Card annual fee" },
                { value: "late_fee", label: "Late fee" },
                { value: "wire_transfer_fee", label: "Wire transfer fee" },
                { value: "atm_fee", label: "ATM fee" },
                { value: "account_maintenance", label: "Account maintenance" },
                { value: "other", label: "Other" },
              ]}
              required
            />
          ) : null}

          <div className="grid grid-cols-2 gap-3">
            <FieldText
              label="Amount (₹)"
              value={amountRupees}
              onChange={setAmountRupees}
              placeholder="0"
              required
            />
            <FieldText
              label="Value date"
              value={valueDate}
              onChange={setValueDate}
              hint="YYYY-MM-DD"
              required
            />
          </div>

          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold text-ink-2">
              Narration <span className="text-rose-600">*</span>
            </span>
            <textarea
              value={narration}
              onChange={(e) => setNarration(e.target.value)}
              required
              rows={3}
              placeholder={
                kind === "transfer"
                  ? "e.g. Top up petty cash drawer"
                  : kind === "statutory_payment"
                    ? "e.g. TDS Q4 26Q · CIN HDFC02612345CIN"
                    : kind === "bank_charge"
                      ? "e.g. NEFT charge · Apr statement"
                      : "Why this movement?"
              }
              className="px-3 py-2 rounded-sm border border-line bg-paper text-sm text-ink placeholder:text-muted focus:border-sky-600 focus:ring-2 focus:ring-sky-100 outline-none resize-vertical"
            />
          </label>

          {meta.alwaysStepUp ? (
            <Alert
              intent="info"
              title="Step-up required"
              description="This kind always needs Founder approval. Operator submissions land in the approval inbox; the OTP UX itself lands in V1.0.x."
            />
          ) : null}

          {error ? (
            <Alert intent="bad" title="Couldn't post the movement" description={error} />
          ) : null}
          {info ? <Alert intent="info" title="Awaiting approval" description={info} /> : null}

          <div className="flex items-center gap-2 pt-2 border-t border-line">
            <button
              type="button"
              onClick={onBack}
              className="inline-flex items-center px-3 py-1.5 rounded-sm text-xs font-semibold bg-paper text-ink-2 border border-line hover:bg-bg"
            >
              Pick a different kind
            </button>
            <span className="flex-1" />
            <button
              type="submit"
              disabled={post.isPending}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-sm text-xs font-semibold bg-ink text-paper border border-ink hover:bg-ink-2 disabled:opacity-50"
            >
              <Send size={13} strokeWidth={2} />
              {post.isPending ? "Posting…" : "Post movement"}
            </button>
          </div>
        </form>
      </div>
    </WorkspaceShell>
  );
}

function needsFrom(kind: Kind): boolean {
  return [
    "transfer",
    "refund_customer",
    "statutory_payment",
    "owner_drawing",
    "loan_repayment",
    "interest_paid",
    "petty_cash_expense",
    "cash_count_adjustment",
    "deposit_paid",
    "bank_charge",
  ].includes(kind);
}
function needsTo(kind: Kind): boolean {
  return [
    "transfer",
    "owner_contribution",
    "loan_disbursement",
    "cash_count_adjustment",
    "deposit_received",
  ].includes(kind);
}
function needsCounterpartyLabel(kind: Kind): boolean {
  return ["loan_disbursement", "loan_repayment", "interest_paid"].includes(kind);
}
function needsOwner(kind: Kind): boolean {
  return ["owner_contribution", "owner_drawing"].includes(kind);
}

// ── small inputs ──────────────────────────────────────────────────

function FieldText({
  label,
  value,
  onChange,
  placeholder,
  hint,
  required,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  hint?: string;
  required?: boolean;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-xs font-semibold text-ink-2">
        {label}
        {required ? <span className="text-rose-600 ml-0.5">*</span> : null}
      </span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className="px-3 py-2 rounded-sm border border-line bg-paper text-sm text-ink placeholder:text-muted focus:border-sky-600 focus:ring-2 focus:ring-sky-100 outline-none"
      />
      {hint ? <span className="text-xs text-muted">{hint}</span> : null}
    </label>
  );
}

function FieldSelect({
  label,
  value,
  onChange,
  options,
  required,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: ReadonlyArray<{ value: string; label: string }>;
  required?: boolean;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-xs font-semibold text-ink-2">
        {label}
        {required ? <span className="text-rose-600 ml-0.5">*</span> : null}
      </span>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="!h-9 !text-sm">
          <SelectValue placeholder="Select…" />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </label>
  );
}
